import React, { useState } from 'react';
import { FiImage, FiPlay, FiExternalLink, FiChevronLeft, FiChevronRight, FiChevronDown, FiAlertCircle } from 'react-icons/fi';
import { WidgetDef, SettingControl, SettingValues, GENERATE_NOTE } from '../types';
import { DemoHead, useWidgetSite } from '../shared';

type Kind = 'Article' | 'Video' | 'Podcast' | 'Webinar' | 'Media coverage';

interface MediaItem { title: string; kind: Kind; date: string; year: number; order: number }

// the category tab each kind sits under
const TAB_OF: Record<Kind, string> = { Article: 'Articles', Video: 'Videos', Podcast: 'Podcasts', Webinar: 'Videos', 'Media coverage': 'Coverage' };
const CATEGORIES = ['All', 'Articles', 'Videos', 'Podcasts', 'Coverage'];
const YEARS = ['All years', '2026', '2025'];
const SORTS = ['Newest first', 'Oldest first', 'Title A–Z'];

const thumbIcon = (kind: Kind) => {
    if (kind === 'Article') return <FiImage />;
    if (kind === 'Media coverage') return <FiExternalLink />;
    return <FiPlay />;
};

const items = (short: string): MediaItem[] => [
    { title: 'Site visit recap — Gold Mountain', kind: 'Article', date: '2 Sep 2026', year: 2026, order: 6 },
    { title: 'Managing Director interview', kind: 'Video', date: '28 Aug 2026', year: 2026, order: 5 },
    { title: `Mining Weekly: ${short} doubles resource at Gold Mountain`, kind: 'Media coverage', date: '27 Aug 2026', year: 2026, order: 4 },
    { title: 'The Resources Podcast, episode 41', kind: 'Podcast', date: '14 Aug 2026', year: 2026, order: 3 },
    { title: 'Quarterly webinar recording', kind: 'Webinar', date: '31 Jul 2026', year: 2026, order: 2 },
    { title: 'Drilling update at Kimberley South', kind: 'Article', date: '22 Jul 2026', year: 2026, order: 1 },
];

// custom rows carry no default, so "Shown as" falls back here until it's changed
const AS_DEFAULT: Record<string, string> = { categoryAs: 'Tabs', yearAs: 'Dropdown' };
const shownAsValue = (s: SettingValues, key: string) => String(s[key] ?? AS_DEFAULT[key]);

// "Shown as Dropdown | Tabs": only one filter can use tabs, so the other filter's Tabs option is greyed out
const shownAs = (key: string, other: string): SettingControl => ({
    kind: 'custom',
    render: ({ settings, set }) => {
        const value = shownAsValue(settings, key);
        return <div className="wd-setting inline">
            <div className="t"><span>Shown as</span></div>
            <div className="wd-segmented wd-seg-partial" role="radiogroup" aria-label="Shown as">
                {['Dropdown', 'Tabs'].map(o => <button key={o} type="button" role="radio" aria-checked={value === o}
                    disabled={o === 'Tabs' && shownAsValue(settings, other) === 'Tabs'}
                    className={value === o ? 'active' : ''} onClick={() => set(key, o)}>{o}</button>)}
            </div>
        </div>;
    },
});

const tabsNote = (s: SettingValues) => {
    if (shownAsValue(s, 'categoryAs') === 'Tabs') return 'Only one filter can use tabs. Category is on tabs, so year is set to a dropdown.';
    if (shownAsValue(s, 'yearAs') === 'Tabs') return 'Only one filter can use tabs. Year is on tabs, so category is set to a dropdown.';
    return 'Only one filter can use tabs.';
};

const MediaCard: React.FC<{ item: MediaItem; thumbs: boolean; date: boolean }> = ({ item, thumbs, date }) =>
    <div className="wd-media-card">
        {thumbs && <div className="thumb">{thumbIcon(item.kind)}</div>}
        <div className="body">
            <strong>{item.title}</strong>
            <span>{item.kind}{date && ` · ${item.date}`}</span>
        </div>
    </div>;

// Carousel (a row of `cols` cards) and Slider (one card) page through the items with arrows and dots
const Pager: React.FC<{ list: MediaItem[]; per: number; slider: boolean; thumbs: boolean; date: boolean }> = ({ list, per, slider, thumbs, date }) => {
    const [page, setPage] = useState(0);
    const pages = Math.max(1, Math.ceil(list.length / per));
    const current = Math.min(page, pages - 1);
    const shown = list.slice(current * per, current * per + per);
    return <div className={`wd-media-pager${slider ? ' slider' : ''}`}>
        <div className="row">
            <button type="button" className="arrow" aria-label="Previous" disabled={current === 0} onClick={() => setPage(current - 1)}><FiChevronLeft /></button>
            <div className="wd-media-grid" style={{ '--cols': per } as React.CSSProperties}>
                {shown.map(i => <MediaCard key={i.title} item={i} thumbs={thumbs} date={date} />)}
            </div>
            <button type="button" className="arrow" aria-label="Next" disabled={current === pages - 1} onClick={() => setPage(current + 1)}><FiChevronRight /></button>
        </div>
        <div className="dots">
            {Array.from({ length: pages }, (_, i) => <button key={i} type="button" aria-label={`Page ${i + 1}`} className={i === current ? 'active' : ''} onClick={() => setPage(i)} />)}
        </div>
    </div>;
};

const NewsDemo: React.FC<{ settings: SettingValues }> = ({ settings: s }) => {
    const { short } = useWidgetSite();
    const [category, setCategory] = useState('All');
    const [year, setYear] = useState('All years');
    const [visitorSort, setVisitorSort] = useState<string | null>(null);
    const sort = s.visitorSort && visitorSort ? visitorSort : String(s.sort);
    const cols = Number(s.columns) || 3;

    const list = items(short)
        .filter(i => !s.filterCategory || category === 'All' || TAB_OF[i.kind] === category)
        .filter(i => !s.filterYear || year === 'All years' || String(i.year) === year)
        .sort((a, b) => {
            if (sort === 'Oldest first') return a.order - b.order;
            if (sort === 'Title A–Z') return a.title.localeCompare(b.title);
            return b.order - a.order;
        });

    const tabs = (options: string[], value: string, onPick: (v: string) => void, label: string) =>
        <div className="wd-media-tabs" role="tablist" aria-label={label}>
            {options.map(o => <button key={o} type="button" role="tab" aria-selected={o === value} className={o === value ? 'active' : ''} onClick={() => onPick(o)}>{o === 'All years' ? 'All' : o}</button>)}
        </div>;
    const dropdown = (options: string[], value: string, onPick: (v: string) => void, label: string, display?: (o: string) => string) =>
        <select className="wd-media-select" aria-label={label} value={value} onChange={e => onPick(e.target.value)}>
            {options.map(o => <option key={o} value={o}>{display ? display(o) : o}</option>)}
        </select>;

    const categoryTabs = s.filterCategory && shownAsValue(s, 'categoryAs') === 'Tabs';
    const yearTabs = s.filterYear && shownAsValue(s, 'yearAs') === 'Tabs';
    const thumbs = !!s.thumbnails;
    const date = !!s.date;

    let body: React.ReactNode;
    if (list.length === 0) body = <p className="wd-media-empty">Nothing here for that filter yet.</p>;
    else if (s.layout === 'List') body = <div className="wd-media-list">{list.map(i => <MediaCard key={i.title} item={i} thumbs={thumbs} date={date} />)}</div>;
    else if (s.layout === 'Carousel') body = <Pager key={`c${cols}${list.length}`} list={list} per={cols} slider={false} thumbs={thumbs} date={date} />;
    else if (s.layout === 'Slider') body = <Pager key={`s${list.length}`} list={list} per={1} slider thumbs={thumbs} date={date} />;
    else body = <div className="wd-media-grid" style={{ '--cols': cols } as React.CSSProperties}>
        {list.map(i => <MediaCard key={i.title} item={i} thumbs={thumbs} date={date} />)}
    </div>;

    return <>
        <DemoHead title="News & media" sub="Articles, videos, podcasts and coverage" />
        <div className="wd-media-bar">
            {categoryTabs && tabs(CATEGORIES, category, setCategory, 'Category')}
            {yearTabs && tabs(YEARS, year, setYear, 'Year')}
            <span className="sp" />
            {s.filterCategory && !categoryTabs && dropdown(CATEGORIES, category, setCategory, 'Category', o => o === 'All' ? 'All types' : o)}
            {s.filterYear && !yearTabs && dropdown(YEARS, year, setYear, 'Year')}
            {s.visitorSort
                ? dropdown(SORTS, sort, setVisitorSort, 'Sort')
                : <span className="wd-media-select static">{sort} <FiChevronDown /></span>}
        </div>
        {body}
    </>;
};

// spec 622:5089
const newsMedia: WidgetDef = {
    slug: 'news-media',
    name: 'News & media',
    description: 'Articles, videos, podcasts and media coverage',
    icon: <FiImage />,
    title: 'News & Media Widget',
    installed: true,
    page: '/news-media/',
    views: 880,
    savedCodes: [
        { path: '/', note: 'Carousel · no filters' },
        { path: '/news-media/', note: 'Grid · 3 columns · filters' },
    ],
    demoPath: '/news-media/',
    addNote: 'This code is generated from the settings below. Change a setting and the code updates — paste it once, it stays current.',
    addBlocks: [
        { title: '1 · WordPress shortcode', code: '[investi_news instance="news-media"]' },
        { title: '2 · Or plain HTML', code: '<div data-investi-instance="news-media" data-investi="news"></div>', dark: true },
    ],
    settings: [
        { kind: 'heading', label: 'Layout' },
        { kind: 'segmented', key: 'layout', options: ['List', 'Grid', 'Carousel', 'Slider'], default: 'Grid' },
        // List and Slider show one item per row
        { kind: 'segmented', key: 'columns', label: 'Columns', options: ['2', '3', '4'], default: '3', inline: true, disabled: s => s.layout === 'List' || s.layout === 'Slider' },
        { kind: 'toggle', key: 'thumbnails', label: 'Show thumbnails', default: true },
        { kind: 'heading', label: 'Filters & sorting' },
        { kind: 'toggle', key: 'filterCategory', label: 'Filter by category', default: true },
        shownAs('categoryAs', 'yearAs'),
        { kind: 'toggle', key: 'filterYear', label: 'Filter by year', default: true },
        shownAs('yearAs', 'categoryAs'),
        { kind: 'custom', render: ({ settings }) => <div className="wd-info"><FiAlertCircle /> {tabsNote(settings)}</div> },
        { kind: 'select', key: 'sort', label: 'Sort by', options: SORTS, default: 'Newest first' },
        { kind: 'toggle', key: 'visitorSort', label: 'Let visitors change the sort', default: false },
        { kind: 'heading', label: 'What it shows' },
        { kind: 'toggle', key: 'date', label: 'Date on each item', default: true },
    ],
    generateNote: GENERATE_NOTE,
    Demo: NewsDemo,
};

export default newsMedia;
